import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput, materialVectorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import {rgbBlendType} from "#material/nodes/color/mix-rgb/validation"
import {renameBlendTypesToBlender} from "#material/nodes/converter/mix/validation"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import type {DataType, FactorMode} from "#material/nodes/converter/mix/types"
import {dataTypeSchema, factorModeSchema} from "#material/nodes/converter/mix/types"
import type {RgbBlendType} from "#material/three/three-utils"

export interface MixSockets extends MaterialNodeSockets {
    inputs: {
        aColor: "color" // found in inputs: color,point,float; found in settings: color; inferred from data: float3
        aFloat: "float" // found in inputs: -; found in settings: float; inferred from data: float
        aVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        bColor: "color" // found in inputs: color,float; found in settings: color; inferred from data: float3
        bFloat: "float" // found in inputs: -; found in settings: float; inferred from data: float
        bVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        factorFloat: "float" // found in inputs: float,color; found in settings: float; inferred from data: float
        factorVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    settings: {
        aColor: "color" // found in inputs: color,point,float; found in settings: color; inferred from data: float3
        aFloat: "float" // found in inputs: -; found in settings: float; inferred from data: float
        aVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        bColor: "color" // found in inputs: color,float; found in settings: color; inferred from data: float3
        bFloat: "float" // found in inputs: -; found in settings: float; inferred from data: float
        bVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        clampFactor: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        clampResult: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        factorFloat: "float" // found in inputs: float,color; found in settings: float; inferred from data: float
        factorVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        resultColor: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        resultFloat: "float" // found in inputs: -; found in settings: float; inferred from data: float
        resultVector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    settingsExtensions: {
        blendType?: RgbBlendType // found in inputs: -; found in settings: string; inferred from data: string
        dataType?: DataType // found in inputs: -; found in settings: string; inferred from data: string
        factorMode?: FactorMode // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        resultColor?: "color"
        resultFloat?: "float"
        resultVector?: "vector"
    }
}

const RGBA_ONLY = {setting: "dataType", oneOf: ["RGBA", undefined]} as const
const FLOAT_ONLY = {setting: "dataType", oneOf: ["FLOAT"]} as const
const VECTOR_ONLY = {setting: "dataType", oneOf: ["VECTOR"]} as const

export const mixDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeMix,
    },
    nodeClass: "Mix",
    versionChain: versionChain([renameBlendTypesToBlender]),
    ui: {
        label: "Mix",
        color: "#8d802f",
        items: [
            {name: "resultColor", valueType: "color", label: "Color", output: true, visibleWhen: RGBA_ONLY},
            {name: "resultFloat", valueType: "float", label: "Value", output: true, visibleWhen: FLOAT_ONLY},
            {name: "resultVector", valueType: "vector", label: "Vector", output: true, visibleWhen: VECTOR_ONLY},
            {
                name: "dataType",
                defaultValue: "RGBA",
                label: "Type",
                valueType: "string",
                options: [
                    {label: "Color", value: "RGBA"},
                    {label: "Float", value: "FLOAT"},
                    {label: "Vector", value: "VECTOR"},
                ],
                input: false,
                setting: true,
            },
            {name: "factorFloat", valueType: "float", label: "Fac", range: {min: 0, max: 1}, input: true, setting: true, defaultValue: 0.5},
            {name: "aColor", valueType: "color", label: "Color1", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}, visibleWhen: RGBA_ONLY},
            {name: "bColor", valueType: "color", label: "Color2", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}, visibleWhen: RGBA_ONLY},
            {name: "aFloat", valueType: "float", label: "A", input: true, setting: true, defaultValue: 0, visibleWhen: FLOAT_ONLY},
            {name: "bFloat", valueType: "float", label: "B", input: true, setting: true, defaultValue: 0, visibleWhen: FLOAT_ONLY},
            {name: "aVector", valueType: "vector", label: "A", input: true, setting: true, visibleWhen: VECTOR_ONLY},
            {name: "bVector", valueType: "vector", label: "B", input: true, setting: true, visibleWhen: VECTOR_ONLY},
            {
                name: "blendType",
                visibleWhen: RGBA_ONLY,
                defaultValue: "MIX",
                label: "Blend type",
                valueType: "string",
                options: [
                    {label: "Mix", value: "MIX"},
                    {label: "Darken", value: "DARKEN"},
                    {label: "Multiply", value: "MULTIPLY"},
                    {label: "Color Burn", value: "BURN"},
                    {label: "Lighten", value: "LIGHTEN"},
                    {label: "Screen", value: "SCREEN"},
                    {label: "Color Dodge", value: "DODGE"},
                    {label: "Add", value: "ADD"},
                    {label: "Overlay", value: "OVERLAY"},
                    {label: "Soft Light", value: "SOFT_LIGHT"},
                    {label: "Linear Light", value: "LINEAR_LIGHT"},
                    {label: "Difference", value: "DIFFERENCE"},
                    {label: "Exclusion", value: "EXCLUSION"},
                    {label: "Subtract", value: "SUBTRACT"},
                    {label: "Divide", value: "DIVIDE"},
                    {label: "Hue", value: "HUE"},
                    {label: "Saturation", value: "SATURATION"},
                    {label: "Color", value: "COLOR"},
                    {label: "Value", value: "VALUE"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "factorMode",
                defaultValue: "UNIFORM",
                label: "Factor Mode",
                valueType: "string",
                options: [
                    {label: "Uniform", value: "UNIFORM"},
                    {label: "Non Uniform", value: "NON_UNIFORM"},
                ],
                input: false,
                setting: true,
            },
            {name: "clampResult", label: "Clamp Result", valueType: "boolean", defaultValue: false, input: false, setting: true},
            {name: "clampFactor", label: "Clamp Factor", valueType: "boolean", defaultValue: true, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({
            resultColor: z.optional(materialColorOutput),
            resultFloat: z.optional(materialFloatOutput),
            resultVector: z.optional(materialVectorOutput),
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                aColor: optionalMaterialInput,
                aFloat: optionalMaterialInput,
                aVector: optionalMaterialInput,
                bColor: optionalMaterialInput,
                bFloat: optionalMaterialInput,
                bVector: optionalMaterialInput,
                factorFloat: optionalMaterialInput,
                factorVector: optionalMaterialInput,
            }),
            settings: z.strictObject({
                aColor: z.optional(color),
                aFloat: z.optional(z.number()),
                aVector: z.optional(vec3),
                bColor: z.optional(color),
                bFloat: z.optional(z.number()),
                bVector: z.optional(vec3),
                blendType: z.optional(rgbBlendType),
                clampFactor: z.optional(z.boolean()),
                clampResult: z.optional(z.boolean()),
                dataType: z.optional(dataTypeSchema),
                factorFloat: z.optional(z.number()),
                factorMode: z.optional(factorModeSchema),
                factorVector: z.optional(vec3),
                resultColor: z.optional(color),
                resultFloat: z.optional(z.number()),
                resultVector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<MixSockets>
